import React, { useEffect, useState } from "react";
import { Select } from "antd";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertFromRaw, convertToRaw, ContentState } from "draft-js";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { getClinicUsers } from "../../../../apis/user";

const STATUS_OPTIONS = [
  { value: "scheduled", label: "Scheduled" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

interface ClinicUser {
  id: number;
  firstName?: string;
  lastName?: string;
}

interface OTFormProps {
  initialData?: any;
  onSave: (data: any) => void;
  onCancel: () => void;
}

const OTForm: React.FC<OTFormProps> = ({ initialData, onSave, onCancel }) => {
  const [clinicUsers, setClinicUsers] = useState<ClinicUser[]>([]);
  const [formData, setFormData] = useState({
    procedureName: initialData?.procedureName || "",
    procedureDate: initialData?.procedureDate || "",
    procedureTime: initialData?.procedureTime || "",
    status: initialData?.status || "scheduled",
    surgeon1: (initialData?.surgeon1 ?? null) as number | null,
    surgeon2: (initialData?.surgeon2 ?? null) as number | null,
    anaesthetists: (initialData?.anaesthetists ?? null) as number | null,
    assistants: (initialData?.assistants ?? null) as number | null,
    findings: initialData?.findings || "",
    bloodLossMl: initialData?.bloodLossMl || "",
    complications: initialData?.complications || "",
    implants: initialData?.implants || "",
    notes: initialData?.notes || "",
  });
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());

  useEffect(() => {
    getClinicUsers().then((res: any) => {
      const raw = res?.data ?? res;
      const data = Array.isArray(raw) ? raw : raw?.data ?? [];
      setClinicUsers(Array.isArray(data) ? data : []);
    });
  }, []);

  useEffect(() => {
    const raw = initialData?.operationDetails;
    if (raw) {
      try {
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        const contentState = convertFromRaw(parsed);
        setEditorState(EditorState.createWithContent(contentState));
      } catch {
        setEditorState(
          EditorState.createWithContent(ContentState.createFromText(String(raw)))
        );
      }
    } else {
      setEditorState(EditorState.createEmpty());
    }
  }, [initialData?.operationDetails]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const contentState = editorState.getCurrentContent();
    const operationDetails = JSON.stringify(convertToRaw(contentState));
    onSave({ ...formData, operationDetails });
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Name of the Procedure
          </label>
          <input
            type="text"
            value={formData.procedureName}
            onChange={(e) => setFormData({ ...formData, procedureName: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
            placeholder="Enter procedure name"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">Date</label>
            <input
              type="date"
              value={formData.procedureDate}
              onChange={(e) => setFormData({ ...formData, procedureDate: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Time</label>
            <input
              type="time"
              value={formData.procedureTime}
              onChange={(e) => setFormData({ ...formData, procedureTime: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
        <Select
          style={{ width: "100%" }}
          value={formData.status}
          onChange={(v) => setFormData({ ...formData, status: v })}
          options={STATUS_OPTIONS}
          className="mt-1"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Surgeon 1</label>
          <Select
            style={{ width: "100%" }}
            placeholder="Select primary surgeon"
            value={formData.surgeon1 ?? undefined}
            onChange={(v) => setFormData({ ...formData, surgeon1: v ?? null })}
            allowClear
            options={clinicUsers.map((u) => ({
              value: u.id,
              label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || `User ${u.id}`,
            }))}
            className="mt-1"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Surgeon 2</label>
          <Select
            style={{ width: "100%" }}
            placeholder="Select secondary surgeon"
            value={formData.surgeon2 ?? undefined}
            onChange={(v) => setFormData({ ...formData, surgeon2: v ?? null })}
            allowClear
            options={clinicUsers.map((u) => ({
              value: u.id,
              label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || `User ${u.id}`,
            }))}
            className="mt-1"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Anaesthetists</label>
          <Select
            style={{ width: "100%" }}
            placeholder="Select Anaesthetists"
            value={formData.anaesthetists ?? undefined}
            onChange={(v) => setFormData({ ...formData, anaesthetists: v ?? null })}
            allowClear
            options={clinicUsers.map((u) => ({
              value: u.id,
              label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || `User ${u.id}`,
            }))}
            className="mt-1"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Assistants</label>
          <Select
            style={{ width: "100%" }}
            placeholder="Select assistant"
            value={formData.assistants ?? undefined}
            onChange={(v) => setFormData({ ...formData, assistants: v ?? null })}
            allowClear
            options={clinicUsers.map((u) => ({
              value: u.id,
              label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || `User ${u.id}`,
            }))}
            className="mt-1"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Operation Details
        </label>
        <Editor
          editorState={editorState}
          onEditorStateChange={setEditorState}
          toolbarClassName="border border-gray-300 rounded-t-md"
          wrapperClassName="border border-gray-300 rounded-md"
          editorClassName="p-3 min-h-[200px] border-t border-gray-300"
          toolbar={{
            options: ["inline", "list", "blockType"],
            inline: {
              options: ["bold", "italic", "underline"],
            },
          }}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Findings</label>
          <textarea
            value={formData.findings}
            onChange={(e) => setFormData({ ...formData, findings: e.target.value })}
            rows={3}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Blood Loss (ml)</label>
          <input
            type="number"
            min={0}
            value={formData.bloodLossMl}
            onChange={(e) => setFormData({ ...formData, bloodLossMl: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Complications</label>
          <textarea
            value={formData.complications}
            onChange={(e) => setFormData({ ...formData, complications: e.target.value })}
            rows={2}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Implants</label>
          <textarea
            value={formData.implants}
            onChange={(e) => setFormData({ ...formData, implants: e.target.value })}
            rows={2}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Notes</label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          rows={3}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
        />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
        >
          Save OT
        </button>
      </div>
    </form>
  );
};

export default OTForm;
